import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAll';
import { getCurrentUser } from '../../lib/auth';
// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

const CATEGORY_META = {
  bug:      { icon: '🐛', label: 'Bug report',    color: 'var(--feedback-error-bg)', textColor: '#991b1b' },
  feedback: { icon: '💡', label: 'Feedback',       color: '#fef9c3', textColor: '#854d0e' },
  question: { icon: '❓', label: 'Question',        color: '#eff6ff', textColor: '#1e40af' },
  other:    { icon: '💬', label: 'Other',           color: '#f3f4f6', textColor: '#374151' },
};

const STATUS_META = {
  open:     { label: 'Open',     bg: '#fee2e2', color: '#991b1b' },
  replied:  { label: 'Replied',  bg: '#d1fae5', color: '#065f46' },
  closed:   { label: 'Closed',   bg: '#f3f4f6', color: '#6b7280' },
};

function pill(bg, color) {
  return {
    fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '4px 12px', borderRadius: 'var(--radius-md)',
    border: 'none', background: bg, color, cursor: 'pointer',
    fontFamily: 'var(--font-body)',
  };
}

// ── StatusBadge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span style={{
      fontSize: 'var(--font-size-xxs)', fontWeight: 700, padding: '2px 8px', borderRadius: 'var(--radius-10)',
      background: m.bg, color: m.color,
    }}>
      {m.label}
    </span>
  );
}

// ── FeedbackRow ───────────────────────────────────────────────────────────────
function FeedbackRow({ item, isSelected, onClick }) {
  const meta = CATEGORY_META[item.category] || CATEGORY_META.other;
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 'var(--space-md)',
        width: '100%', padding: '14px 16px', textAlign: 'left',
        background: isSelected ? '#fef9f0' : 'var(--brand-white)',
        borderBottom: '1px solid var(--brand-cream)', border: 'none',
        borderLeft: isSelected ? '3px solid var(--feedback-warning)' : '3px solid transparent',
        cursor: 'pointer', transition: 'background 0.1s',
        fontFamily: 'var(--font-body)',
        // Ensure good tap target height
        minHeight: 64,
      }}
      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#faf8f5'; }}
      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 'var(--radius-10)', flexShrink: 0,
        background: meta.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 'var(--font-size-lg)',
      }}>
        {meta.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-6)', marginBottom: 3, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, color: meta.textColor, background: meta.color, padding: '1px 6px', borderRadius: 6 }}>
            {meta.label}
          </span>
          <StatusBadge status={item.status} />
          {item.portal && (
            <span style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
              {item.portal} · {timeAgo(item.created_at)}
            </span>
          )}
        </div>
        <p style={{
          fontSize: 'var(--font-size-base)', color: 'var(--neutral-800)', margin: 'var(--space-none)',
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          lineHeight: 1.45,
        }}>
          {item.message}
        </p>
        {item.email && (
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', margin: '4px 0 0' }}>
            📧 {item.email}
          </p>
        )}
      </div>
    </button>
  );
}

// ── ReplyPanel ────────────────────────────────────────────────────────────────
function ReplyPanel({ item, onRefresh, onBack, isMobile }) {
  const [reply,   setReply]   = useState('');
  const [sending, setSending] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(true);
  const meta = CATEGORY_META[item.category] || CATEGORY_META.other;

  useEffect(() => { loadReplies(); }, [item.id]);

  async function loadReplies() {
    setLoadingReplies(true);
    const data = await fetchAllRows(() => supabase
      .from('feedback_replies')
      .select('*')
      .eq('feedback_id', item.id));
    data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    setReplies(data);
    setLoadingReplies(false);
  }

  async function sendReply() {
    if (!reply.trim()) return;
    setSending(true);
    const user = await getCurrentUser();
    await supabase.from('feedback_replies').insert({
      feedback_id: item.id,
      message: reply.trim(),
      admin_id: user?.id,
      is_admin: true,
    });
    await supabase.from('feedback').update({ status: 'replied' }).eq('id', item.id);
    setReply('');
    setSending(false);
    loadReplies();
    onRefresh();
  }

  async function updateStatus(newStatus) {
    await supabase.from('feedback').update({ status: newStatus }).eq('id', item.id);
    onRefresh();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'var(--font-body)' }}>

      {/* Header */}
      <div style={{ padding: isMobile ? '14px 16px' : '20px 24px', borderBottom: '1px solid var(--brand-sand)', flexShrink: 0 }}>

        {/* Mobile back button */}
        {isMobile && (
          <button
            onClick={onBack}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-xs)',
              fontSize: 'var(--font-size-base)', color: 'var(--feedback-warning)', fontWeight: 600,
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0 0 10px', fontFamily: 'var(--font-body)',
            }}
          >
            ← Back
          </button>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--font-size-xl)' }}>{meta.icon}</span>
          <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: meta.textColor, background: meta.color, padding: '2px 8px', borderRadius: 6 }}>
            {meta.label}
          </span>
          <StatusBadge status={item.status} />
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', marginLeft: 'auto' }}>{timeAgo(item.created_at)}</span>
        </div>

        <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-800)', margin: '0 0 8px', lineHeight: 1.55 }}>
          {item.message}
        </p>

        <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
          {item.email && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-600)', background: 'var(--brand-cream)', padding: '3px 10px', borderRadius: 'var(--radius-md)' }}>
              📧 {item.email}
            </span>
          )}
          {item.portal && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-600)', background: 'var(--brand-cream)', padding: '3px 10px', borderRadius: 'var(--radius-md)' }}>
              🖥️ {item.portal} portal
            </span>
          )}
          <div style={{ marginLeft: isMobile ? 0 : 'auto', display: 'flex', gap: 'var(--space-6)', marginTop: isMobile ? 4 : 0, width: isMobile ? '100%' : 'auto' }}>
            {item.status !== 'closed' && (
              <button onClick={() => updateStatus('closed')} style={pill('#f3f4f6', '#374151')}>Mark closed</button>
            )}
            {item.status === 'closed' && (
              <button onClick={() => updateStatus('open')} style={pill('#fef3c7', '#92400e')}>Reopen</button>
            )}
          </div>
        </div>
      </div>

      {/* Replies thread */}
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px 16px' : '16px 24px', display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
        {loadingReplies ? (
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textAlign: 'center', marginTop: 'var(--space-xl)' }}>Loading…</p>
        ) : replies.length === 0 ? (
          <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-400)', textAlign: 'center', marginTop: 'var(--space-xl)', fontStyle: 'italic' }}>
            No replies yet. Write one below.
          </p>
        ) : (
          replies.map(r => (
            <div key={r.id} style={{ alignSelf: r.is_admin ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
              <div style={{
                background: r.is_admin ? 'var(--neutral-800)' : 'var(--brand-cream)',
                color: r.is_admin ? '#fef9f0' : 'var(--neutral-800)',
                borderRadius: r.is_admin ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                padding: '10px 14px', fontSize: 'var(--font-size-base)', lineHeight: 1.5,
              }}>
                {r.message}
              </div>
              <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '4px 4px 0', textAlign: r.is_admin ? 'right' : 'left' }}>
                {r.is_admin ? 'You' : 'User'} · {timeAgo(r.created_at)}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Reply input */}
      {item.status !== 'closed' && (
        <div style={{ padding: isMobile ? '12px 16px' : '14px 20px', borderTop: '1px solid var(--brand-sand)', flexShrink: 0 }}>
          {!item.email && (
            <p style={{ fontSize: 10.5, color: 'var(--feedback-warning)', margin: '0 0 8px', background: '#fffbeb', padding: '6px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--feedback-warning-border)' }}>
              ⚠️ No email provided, reply won't be sent externally, but is saved here.
            </p>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'flex-end' }}>
            <textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder={item.email ? `Reply to ${item.email}…` : 'Add an internal note…'}
              rows={isMobile ? 3 : 2}
              style={{
                flex: 1, padding: '10px 14px', border: '1px solid var(--brand-sand)',
                borderRadius: 'var(--radius-lg)', fontSize: 'var(--font-size-base)', resize: 'none',
                fontFamily: 'inherit', outline: 'none', lineHeight: 1.45,
                // Prevent iOS zoom on focus (font-size must be >= 16px or use transform trick)
                fontSize: 'var(--font-size-lg)',
              }}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendReply(); }}
            />
            <button
              onClick={sendReply}
              disabled={!reply.trim() || sending}
              style={{
                padding: isMobile ? '12px 18px' : '10px 16px',
                borderRadius: 'var(--radius-lg)', border: 'none',
                background: reply.trim() ? 'var(--neutral-800)' : 'var(--brand-sand)',
                color: reply.trim() ? 'var(--brand-white)' : 'var(--neutral-400)',
                fontSize: 'var(--font-size-base)', fontWeight: 700,
                cursor: reply.trim() ? 'pointer' : 'default',
                flexShrink: 0, fontFamily: 'inherit',
                // Larger tap target on mobile
                minWidth: isMobile ? 64 : 'auto',
              }}
            >
              {sending ? '…' : 'Send'}
            </button>
          </div>
          {!isMobile && (
            <p style={{ fontSize: 'var(--font-size-xxs)', color: 'var(--neutral-400)', margin: '5px 0 0' }}>⌘ + Enter to send</p>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
export default function FeedbackSection({ isMobile }) {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [filter,    setFilter]    = useState('all');
  const [catFilter, setCatFilter] = useState('all');

  // On mobile, whether the detail pane is showing (vs the list)
  const showDetail = isMobile && selected !== null;

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    // Paged: the status tallies below are counted off this array.
    const data = await fetchAllRows(() => supabase.from('feedback').select('*'));
    data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setItems(data);
    setLoading(false);
  }

  const handleSelect = useCallback((item) => {
    setSelected(item);
  }, []);

  const handleBack = useCallback(() => {
    setSelected(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    load();
    if (selected) {
      const { data } = await supabase.from('feedback').select('*').eq('id', selected.id).single();
      if (data) setSelected(data);
    }
  }, [selected]);

  const filtered = items.filter(i => {
    if (filter !== 'all' && i.status !== filter) return false;
    if (catFilter !== 'all' && i.category !== catFilter) return false;
    return true;
  });

  const openCount = items.filter(i => i.status === 'open').length;

  // ── Mobile layout: full-screen list OR full-screen detail ──
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'var(--font-body)', overflow: 'hidden' }}>

        {/* List view */}
        <div style={{
          display: showDetail ? 'none' : 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}>
          {/* List header */}
          <div style={{ padding: '16px 16px 10px', borderBottom: '1px solid var(--brand-sand)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-10)' }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)', flex: 1 }}>
                Feedback
              </h2>
              {openCount > 0 && (
                <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, background: 'var(--feedback-error-bg)', color: '#991b1b', padding: '2px 8px', borderRadius: 'var(--radius-10)' }}>
                  {openCount} open
                </span>
              )}
              <button onClick={load} style={{ fontSize: 'var(--font-size-xl)', color: 'var(--neutral-500)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px' }} title="Refresh">
                ↻
              </button>
            </div>

            {/* Status filter */}
            <div style={{ display: 'flex', gap: 'var(--space-6)', marginBottom: 'var(--space-sm)', flexWrap: 'wrap' }}>
              {['all', 'open', 'replied', 'closed'].map(s => (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  style={{
                    fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '5px 12px', borderRadius: 'var(--radius-md)',
                    border: '1px solid', cursor: 'pointer',
                    background: filter === s ? 'var(--neutral-800)' : 'var(--brand-white)',
                    color: filter === s ? 'var(--brand-white)' : 'var(--neutral-500)',
                    borderColor: filter === s ? 'var(--neutral-800)' : 'var(--brand-sand)',
                    fontFamily: 'var(--font-body)',
                    // Good tap target
                    minHeight: 32,
                  }}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            {/* Category filter */}
            <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
              {['all', 'bug', 'feedback', 'question', 'other'].map(c => (
                <button
                  key={c}
                  onClick={() => setCatFilter(c)}
                  style={{
                    fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '5px 12px', borderRadius: 'var(--radius-md)',
                    border: '1px solid', cursor: 'pointer',
                    background: catFilter === c ? 'var(--feedback-warning-bg)' : 'var(--brand-white)',
                    color: catFilter === c ? 'var(--feedback-warning-ink)' : 'var(--neutral-500)',
                    borderColor: catFilter === c ? 'var(--feedback-warning-border)' : 'var(--brand-sand)',
                    fontFamily: 'var(--font-body)',
                    minHeight: 32,
                  }}
                >
                  {c === 'all' ? 'All' : (CATEGORY_META[c]?.icon + ' ' + CATEGORY_META[c]?.label)}
                </button>
              ))}
            </div>
          </div>

          {/* List */}
          <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {loading ? (
              <div style={{ padding: 30, textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center' }}>
                <p style={{ fontSize: 'var(--font-size-h2)', margin: '0 0 10px' }}>💬</p>
                <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>No feedback here yet.</p>
              </div>
            ) : (
              filtered.map(item => (
                <FeedbackRow
                  key={item.id}
                  item={item}
                  isSelected={false}
                  onClick={() => handleSelect(item)}
                />
              ))
            )}
          </div>
        </div>

        {/* Detail view */}
        {showDetail && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <ReplyPanel
              key={selected.id}
              item={selected}
              onRefresh={handleRefresh}
              onBack={handleBack}
              isMobile={true}
            />
          </div>
        )}
      </div>
    );
  }

  // ── Desktop layout: side-by-side ──
  return (
    <div style={{ display: 'flex', height: '100%', fontFamily: 'var(--font-body)', overflow: 'hidden' }}>

      {/* Left list pane */}
      <div style={{ width: 340, flexShrink: 0, borderRight: '1px solid var(--brand-sand)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* List header */}
        <div style={{ padding: '20px 16px 12px', borderBottom: '1px solid var(--brand-sand)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, color: 'var(--neutral-800)', margin: 'var(--space-none)', flex: 1 }}>
              Feedback
            </h2>
            {openCount > 0 && (
              <span style={{ fontSize: 'var(--font-size-xxs)', fontWeight: 700, background: 'var(--feedback-error-bg)', color: '#991b1b', padding: '2px 8px', borderRadius: 'var(--radius-10)' }}>
                {openCount} open
              </span>
            )}
            <button
              onClick={load}
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-500)', background: 'none', border: 'none', cursor: 'pointer' }}
              title="Refresh"
            >
              ↻
            </button>
          </div>

          {/* Status filter */}
          <div style={{ display: 'flex', gap: 'var(--space-xs)', marginBottom: 'var(--space-sm)', flexWrap: 'wrap' }}>
            {['all', 'open', 'replied', 'closed'].map(s => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                style={{
                  fontSize: 'var(--font-size-xxs)', fontWeight: 600, padding: '3px 10px', borderRadius: 'var(--radius-md)',
                  border: '1px solid', cursor: 'pointer',
                  background: filter === s ? 'var(--neutral-800)' : 'var(--brand-white)',
                  color: filter === s ? 'var(--brand-white)' : 'var(--neutral-500)',
                  borderColor: filter === s ? 'var(--neutral-800)' : 'var(--brand-sand)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>

          {/* Category filter */}
          <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
            {['all', 'bug', 'feedback', 'question', 'other'].map(c => (
              <button
                key={c}
                onClick={() => setCatFilter(c)}
                style={{
                  fontSize: 'var(--font-size-xxs)', fontWeight: 600, padding: '3px 10px', borderRadius: 'var(--radius-md)',
                  border: '1px solid', cursor: 'pointer',
                  background: catFilter === c ? 'var(--feedback-warning-bg)' : 'var(--brand-white)',
                  color: catFilter === c ? 'var(--feedback-warning-ink)' : 'var(--neutral-500)',
                  borderColor: catFilter === c ? 'var(--feedback-warning-border)' : 'var(--brand-sand)',
                  fontFamily: 'var(--font-body)',
                }}
              >
                {c === 'all' ? 'All' : (CATEGORY_META[c]?.icon + ' ' + CATEGORY_META[c]?.label)}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--neutral-400)', fontSize: 'var(--font-size-base)' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center' }}>
              <p style={{ fontSize: 'var(--font-size-h2)', margin: '0 0 10px' }}>💬</p>
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>No feedback here yet.</p>
            </div>
          ) : (
            filtered.map(item => (
              <FeedbackRow
                key={item.id}
                item={item}
                isSelected={selected?.id === item.id}
                onClick={() => handleSelect(item)}
              />
            ))
          )}
        </div>
      </div>

      {/* Right detail pane */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {selected ? (
          <ReplyPanel
            key={selected.id}
            item={selected}
            onRefresh={handleRefresh}
            isMobile={false}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 'var(--space-10)' }}>
            <span style={{ fontSize: 'var(--font-size-h1)' }}>👈</span>
            <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--neutral-400)', margin: 'var(--space-none)' }}>Select a message to read and reply</p>
          </div>
        )}
      </div>
    </div>
  );
}